import {Component, effect, inject, signal} from '@angular/core';
import {
  AnimalButtonComponent
} from "src/app/presentation/main-view/information-center/animals/animal-button/animal-button.component";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {AnimalApiClient} from "src/app/infrastructure/moovement-api/web-api/animal.api-client";
import {
  AnimalDetailsContainerComponent
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-details-container/animal-details-container.component";
import {AnimalsStore} from "src/app/domain/assets/animals/animals.store";
import {AggregatedAssetStateStore} from "src/app/domain/assets/aggregated-state/aggregated-asset-state.store";
import {Animal} from "src/app/infrastructure/moovement-api/models/asset";
import {ActivatedRoute, Router} from "@angular/router";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs";

@Component({
  selector: 'app-animal-relationships',
  imports: [
    AnimalButtonComponent,
    GenericButtonComponent,
    SpinnerComponent,
    AnimalDetailsContainerComponent
  ],
  templateUrl: './animal-relationships.html',
  styleUrl: './animal-relationships.css',
})
export class AnimalRelationships {

  canBeAltered = false;
  canBeDeleted = false;
  loading = false;

  animals = signal<Animal[]>([])
  private readonly animalStore = inject(AnimalsStore)
  private readonly aggregatedAssetStateStore = inject(AggregatedAssetStateStore)
  private readonly route = inject(ActivatedRoute)
  currentAnimalId = toSignal(
    this.route.paramMap.pipe(map(p => p.get('id') ?? undefined))
  )
  animal = signal<Animal | undefined>(undefined)

  constructor(
    protected dataSynchronisationService: DataSynchronisationService,
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService,
    private readonly animalApiClient: AnimalApiClient,
    private readonly router: Router
  ) {
    effect(() => {
      const id = this.currentAnimalId();
      if (!id) return;
      this.animal.set(this.animalStore.entityMap()[id]);
      this.animalApiClient.getAvailableRelationships(id).subscribe({
        next: animals => this.animals.set(animals)
      });
    });
  }

  protected canBeParent(animal: Animal) {
    return animal.type == undefined || ['UNKNOWN', 'HEIFER', 'COW', 'BULL'].includes(animal.type)
  }

  protected canBeChild(animal: Animal) {
    const childsBirthYear = this.animal()?.yearOfBirth
    const parentsBirthYear = animal?.yearOfBirth
    if ([childsBirthYear, parentsBirthYear].some(it => it == undefined)) return true;
    return Number.parseInt(<string>childsBirthYear) > Number.parseInt(<string>parentsBirthYear)
  }

  protected markAs(id: string, relationship: string) {
    const currentId = this.currentAnimalId()!;
    switch (relationship) {
      case 'parent':
        this.animalApiClient.registerParentChildRelationship(currentId, id).subscribe({
          next: registered => {
            const childState = this.aggregatedAssetStateStore.entityMap()[currentId];
            if (childState) {
              this.aggregatedAssetStateStore.setEntity({
                ...childState,
                parents: [...(childState.parents || []), registered.parent]
              });
            }
            const parentState = this.aggregatedAssetStateStore.entityMap()[id];
            if (parentState) {
              this.aggregatedAssetStateStore.setEntity({
                ...parentState,
                children: [...(parentState.children || []), registered.child]
              });
            }
            this.router.navigate(['/main/animal-details', this.animal()?.id]);
          }
        })
        break;
      case 'child':
        this.animalApiClient.registerParentChildRelationship(id, currentId).subscribe({
          next: registered => {
            const parentState = this.aggregatedAssetStateStore.entityMap()[currentId];
            if (parentState) {
              this.aggregatedAssetStateStore.setEntity({
                ...parentState,
                children: [...(parentState.children || []), registered.child]
              });
            }
            const childState = this.aggregatedAssetStateStore.entityMap()[id];
            if (childState) {
              this.aggregatedAssetStateStore.setEntity({
                ...childState,
                parents: [...(childState.parents || []), registered.parent]
              });
            }
            this.router.navigate(['/main/animal-details', this.animal()?.id]);
          }
        })
        break;
    }
  }
}
